import React from 'react';

export const COMPACT_LAYOUT_MAX_WIDTH = 768;
export const COMPACT_LAYOUT_QUERY = '(max-width: 768px), (max-width: 1024px) and (max-height: 480px)';

export function resolveCompactWorkspaceLayout({ containerWidth, viewportMatches }) {
  const hasMeasuredContainer = Number.isFinite(containerWidth) && containerWidth > 0;
  return Boolean(
    viewportMatches
    || (hasMeasuredContainer && containerWidth <= COMPACT_LAYOUT_MAX_WIDTH)
  );
}

function readContainerWidth(container) {
  const width = container?.getBoundingClientRect?.().width;
  return Number.isFinite(width) ? width : null;
}

function subscribeMediaQuery(mediaQuery, listener) {
  if (typeof mediaQuery?.addEventListener === 'function') {
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }
  if (typeof mediaQuery?.addListener === 'function') {
    mediaQuery.addListener(listener);
    return () => mediaQuery.removeListener(listener);
  }
  return () => {};
}

function resolveMediaQuery(windowObject) {
  return typeof windowObject?.matchMedia === 'function'
    ? windowObject.matchMedia(COMPACT_LAYOUT_QUERY)
    : null;
}

function subscribeContainerResize(windowObject, container, listener) {
  if (typeof windowObject?.ResizeObserver !== 'function') {
    return () => {};
  }
  const resizeObserver = new windowObject.ResizeObserver(listener);
  resizeObserver.observe(container);
  return () => resizeObserver.disconnect();
}

function subscribeWindowResize(windowObject, listener) {
  if (typeof windowObject?.addEventListener !== 'function') {
    return () => {};
  }
  windowObject.addEventListener('resize', listener);
  return () => windowObject.removeEventListener('resize', listener);
}

function createCompactLayoutUpdater({ container, getViewportMatches, onChange }) {
  let previousCompactLayout;
  return () => {
    const nextCompactLayout = resolveCompactWorkspaceLayout({
      containerWidth: readContainerWidth(container),
      viewportMatches: getViewportMatches(),
    });
    if (nextCompactLayout === previousCompactLayout) {
      return;
    }
    previousCompactLayout = nextCompactLayout;
    onChange(nextCompactLayout);
  };
}

export function observeCompactWorkspaceLayout({ container, onChange, windowObject }) {
  if (!container || typeof onChange !== 'function') {
    return () => {};
  }

  const mediaQuery = resolveMediaQuery(windowObject);
  let viewportMatches = Boolean(mediaQuery?.matches);
  const update = createCompactLayoutUpdater({
    container,
    getViewportMatches: () => viewportMatches,
    onChange,
  });
  const handleMediaChange = (event) => {
    viewportMatches = Boolean(event.matches);
    update();
  };
  const unsubscribeMediaQuery = subscribeMediaQuery(mediaQuery, handleMediaChange);
  const unsubscribeContainerResize = subscribeContainerResize(windowObject, container, update);
  const unsubscribeWindowResize = subscribeWindowResize(windowObject, update);

  update();

  return () => {
    unsubscribeContainerResize();
    unsubscribeMediaQuery();
    unsubscribeWindowResize();
  };
}

export default function useCompactWorkspaceLayout(containerRef) {
  const [isCompactLayout, setIsCompactLayout] = React.useState(false);

  React.useEffect(() => observeCompactWorkspaceLayout({
    container: containerRef.current,
    onChange: setIsCompactLayout,
    windowObject: typeof window === 'undefined' ? null : window,
  }), [containerRef]);

  return isCompactLayout;
}
